import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { db } from '../config/firebaseConfig';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useAnalytics } from '../hooks/useAnalytics';
import WelcomeStep from '../components/onboarding/WelcomeStep';
import BusinessInfoStep from '../components/onboarding/BusinessInfoStep';
import UseCaseStep from '../components/onboarding/UseCaseStep';
import CompletionStep from '../components/onboarding/CompletionStep';
import { ThemeLogo } from '../components/ThemeLogo';

const Onboarding = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState({});
  
  // Use onboarding context
  const {
    currentStep,
    onboardingData,
    updateOnboardingData,
    goToNextStep,
    goToPreviousStep,
    initializeOnboarding,
    trackOnboardingCompleted
  } = useOnboarding();

  const totalSteps = 4;

  useEffect(() => {
    // Load user data and initialize onboarding
    const user = authService.getCurrentUser();
    if (user) {
      setUserData({
        name: user.displayName || user.email?.split('@')[0] || 'User',
        email: user.email,
        uid: user.uid
      });
    }
    
    // Initialize onboarding analytics
    initializeOnboarding();

    // Manipulate browser history to ensure back button goes to landing page
    // Add a history entry for landing page so back button works correctly
    window.history.pushState({ from: 'landing' }, '', '/');
    window.history.pushState({ from: 'onboarding' }, '', '/onboarding');

    // Handle browser back button to redirect to landing page
    const handlePopState = (event) => {
      // Always navigate to landing page when back button is clicked
      event.preventDefault();
      navigate('/');
    };

    // Handle beforeunload to show confirmation dialog
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    // Add event listeners
    window.addEventListener('popstate', handlePopState, true);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('popstate', handlePopState, true);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [initializeOnboarding, navigate]);

  const updateFormData = (stepData) => {
    updateOnboardingData(stepData);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      goToNextStep();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      goToPreviousStep();
    }
  };

  const completeOnboarding = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      const user = authService.getCurrentUser();
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }

      // Update user document in Firestore using email as document ID
      const userDocRef = doc(db, 'users', user.email);
      
      // Check if user document exists
      const userDoc = await getDoc(userDocRef);
      
      // Complete onboarding data
      const updateData = {
        ...onboardingData,
        first_login: false,
        completedAt: new Date().toISOString()
      };

      if (userDoc.exists()) {
        await updateDoc(userDocRef, updateData);
      } else {
        // Create new user document if it doesn't exist
        await setDoc(userDocRef, {
          email: user.email,
          uid: user.uid,
          displayName: user.displayName,
          ...updateData
        });
      }

      // Track signup completion (since this is their first time through onboarding)
      trackEvent('sign_up', {
        method: 'onboarding_completion',
        success: true,
        user_id: user.uid,
        company_name: onboardingData.companyName,
        industry: onboardingData.industry,
        company_size: onboardingData.companySize,
        job_title: onboardingData.jobTitle,
        use_case: onboardingData.primaryUseCase
      });

      
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [onboardingData, trackEvent]);

  // Automatically complete onboarding when user reaches step 4 (completion step)
  useEffect(() => {
    if (currentStep === 4) {
      completeOnboarding();
    }
  }, [currentStep, completeOnboarding]);

  const handleSkipToAgents = () => {
    // Onboarding is already completed when reaching step 4, just navigate
    trackOnboardingCompleted('dashboard');
    navigate('/agents');
  };

  const handleCreateFirstAgent = () => {
    // Onboarding is already completed when reaching step 4, just navigate
    trackOnboardingCompleted('create_agent');
    navigate('/agents/new');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WelcomeStep
            data={onboardingData}
            updateData={updateFormData}
            onNext={handleNext}
            userName={userData.name}
          />
        );
      case 2:
        return (
          <BusinessInfoStep
            data={onboardingData}
            updateData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <UseCaseStep
            data={onboardingData}
            updateData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <CompletionStep
            userName={userData.name}
            onboardingData={onboardingData}
            onCreateAgent={handleCreateFirstAgent}
            onSkip={handleSkipToAgents}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Welcome to 44Agents';
      case 2: return 'Business Information';
      case 3: return 'Use Case & Goals';
      case 4: return 'Create your first AI Agent';
      default: return 'Setup';
    }
  };

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <ThemeLogo className="h-8" />
          <div className="text-center">
            <h1 className="text-2xl font-bold text-card-foreground mb-2">
              {getStepTitle()}
            </h1>
          </div>
        </div>



        {/* Step Content */}
        <div className="bg-card rounded-lg border border-border shadow-none p-6">
          {renderCurrentStep()}
        </div>

      </div>
    </div>
  );
};

export default Onboarding;